import axios, { AxiosResponse, AxiosError } from "axios";

import {
  ISocreStrategyOption,
  ISurvey,
  ISurveyAnswer,
  ISurveyDisplayRulesTypes,
  IUserSurveyRequest,
} from "../@types/Surveys";
import { api, s3Axios } from "./api";

interface ISurveyMediaUploadUrlResponse {
  key: string;
  presignedUrl: string;
  expiresInSeconds: number;
  requiredHeaders: Record<string, string>;
}

interface IUploadSurveyAnswerMediaArgs {
  surveyQuestionId: string;
  file: File;
  userSurveyId?: string;
  surveyAnswerId?: string;
  onProgress?: (percent: number) => void;
  patientToken?: string;
}

// EN-5289: Surveys > Answer lets staff act as a patient. The patient's token is
// sent on each request instead of being stored as tokenResponse, which would
// replace the staff session.
function patientAuth(patientToken?: string) {
  return patientToken
    ? { headers: { Authorization: `Bearer ${patientToken}` } }
    : undefined;
}

export type UploadSurveyAnswerMediaResult =
  | { ok: true; key: string }
  | { ok: false; stage: "signed-url"; response: AxiosResponse | undefined }
  | { ok: false; stage: "s3-put"; cause: AxiosError | unknown }
  | { ok: false; stage: "confirm"; response: AxiosResponse | undefined };

export const surveysService = {
  async getAllSurveys(): Promise<AxiosResponse> {
    const url = "/Portal/DynamicSurveys/Surveys";
    const surveys = await api.get(url);
    return surveys;
  },

  async getSurveyById(id: string): Promise<AxiosResponse> {
    const url = `/Portal/DynamicSurveys/Surveys/${id}`;
    const survey = await api.get(url);
    return survey;
  },

  async getSurveyByUserSurveyId(id: string): Promise<AxiosResponse> {
    const url = `/Portal/DynamicSurveys/Surveys/ByUserSurveyId/${id}`;
    const survey = await api.get(url);
    return survey;
  },

  async getQuestionTypes(): Promise<AxiosResponse> {
    const url = `/Portal/DynamicSurveys/SurveyQuestionTypes`;
    const survey = await api.get(url);
    return survey;
  },

  async getSurveyDisplayRules(): Promise<
    AxiosResponse<ISurveyDisplayRulesTypes[]>
  > {
    const url = `/Portal/DynamicSurveys/SurveyDisplayRules`;
    const survey = await api.get(url);
    return survey;
  },

  async getSurveyDisplayRulesComparators(): Promise<AxiosResponse<string[]>> {
    const url = `/Portal/DynamicSurveys/SurveyDisplayRulesComparators`;
    const survey = await api.get(url);
    return survey;
  },

  async addSurvey(survey: ISurvey): Promise<AxiosResponse> {
    const url = "/Portal/DynamicSurveys/Surveys";
    const serverResponse = await api
      .post<ISurvey>(url, survey)
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse) {
      return serverResponse;
    }

    throw Error;
  },

  async assignSurveyToWards(
    surveyId: string,
    wardIds: number[]
  ): Promise<AxiosResponse> {
    const url = `/Portal/DynamicSurveys/Surveys/${surveyId}/AssignWards`;
    const serverResponse = await api
      .put<{ wardIds: number[] }>(url, { wardIds })
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse) {
      return serverResponse;
    }

    throw Error;
  },

  async getAllHospitals(): Promise<AxiosResponse> {
    const url = "/Portal/Wards/GetAllWards";
    const surveys = await api.get(url);
    return surveys;
  },

  async editSurvey(survey: ISurvey): Promise<AxiosResponse> {
    const url = `/Portal/DynamicSurveys/Surveys/${survey.id}`;
    const serverResponse = await api
      .put<ISurvey>(url, survey)
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse) {
      return serverResponse;
    }

    throw Error;
  },

  async getSurveyByUserSurveyIdMobile(
    userSurveyId: string,
    patientToken?: string
  ): Promise<AxiosResponse> {
    const url = `/Portal/DynamicSurveys/Surveys/ByUserSurveyId/${userSurveyId}`;
    const survey = await api
      .get(url, patientAuth(patientToken))
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (survey) {
      return survey;
    }

    throw Error;
  },

  async getScoreStrategiesOptions(): Promise<
    AxiosResponse<ISocreStrategyOption[]>
  > {
    const url = `/Portal/DynamicSurveys/SurveyScoreStrategies`;

    const serverResponse = await api.get(url);

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async getSurveyTypesOptions(): Promise<AxiosResponse<string[]>> {
    const url = `/Portal/DynamicSurveys/SurveyTypes`;

    const serverResponse = await api.get(url);

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async getUserSurveyIdBySurveyId(
    surveyId: string,
    patientId: string
  ): Promise<AxiosResponse<string>> {
    const url = `/Portal/DynamicSurveys/FormUserSurveyId`;

    const serverResponse = await api.post(url, {
      patientId,
      surveyId,
    });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async updateSurveyAnswer(
    surveyAnswerId: string,
    updatesAnswers: ISurveyAnswer[]
  ): Promise<AxiosResponse> {
    const url = `Portal/DynamicSurveys/SurveyAnswer/${surveyAnswerId}`;

    const response = await api.put(url, updatesAnswers);

    return response;
  },

  async requestSurveyMediaUploadUrl(
    surveyQuestionId: string,
    file: File,
    userSurveyId?: string,
    surveyAnswerId?: string,
    patientToken?: string
  ): Promise<AxiosResponse<ISurveyMediaUploadUrlResponse>> {
    const url = `/Portal/DynamicSurveys/Answers/Media/SignedUrl`;
    return api.post<ISurveyMediaUploadUrlResponse>(
      url,
      {
        surveyQuestionId,
        contentType: file.type,
        fileName: file.name,
        sizeBytes: file.size,
      },
      {
        params: { userSurveyId, surveyAnswerId },
        ...patientAuth(patientToken),
      }
    );
  },

  async confirmSurveyMediaUpload(
    key: string,
    patientToken?: string
  ): Promise<AxiosResponse<string>> {
    const url = `/Portal/DynamicSurveys/Answers/Media/Confirm`;
    return api.post<string>(url, { key }, patientAuth(patientToken));
  },

  // Three-step survey-media upload: API issues a presigned URL, browser PUTs
  // directly to S3 (so the long phase never holds an idle TCP socket on our
  // ECS task), API validates magic bytes on completion. Returns a tagged
  // union so the caller can show different error UI per failure stage.
  async uploadSurveyAnswerMedia(
    args: IUploadSurveyAnswerMediaArgs
  ): Promise<UploadSurveyAnswerMediaResult> {
    const {
      surveyQuestionId,
      file,
      userSurveyId,
      surveyAnswerId,
      onProgress,
      patientToken,
    } = args;

    let signedUrlResponse:
      | AxiosResponse<ISurveyMediaUploadUrlResponse>
      | undefined;
    try {
      signedUrlResponse = await surveysService.requestSurveyMediaUploadUrl(
        surveyQuestionId,
        file,
        userSurveyId,
        surveyAnswerId,
        patientToken
      );
    } catch {
      return { ok: false, stage: "signed-url", response: undefined };
    }

    if (
      !signedUrlResponse ||
      signedUrlResponse.status < 200 ||
      signedUrlResponse.status >= 300
    ) {
      return { ok: false, stage: "signed-url", response: signedUrlResponse };
    }

    const { key, presignedUrl, requiredHeaders } = signedUrlResponse.data;

    try {
      await s3Axios.put(presignedUrl, file, {
        headers: {
          "Content-Type": requiredHeaders["Content-Type"] ?? file.type,
        },
        onUploadProgress: onProgress
          ? (event) => {
              const total = event.total ?? 0;
              const percent =
                total > 0 ? Math.round((event.loaded * 100) / total) : 0;
              onProgress(percent);
            }
          : undefined,
      });
    } catch (cause) {
      return { ok: false, stage: "s3-put", cause };
    }

    let confirmResponse: AxiosResponse<string> | undefined;
    try {
      confirmResponse = await surveysService.confirmSurveyMediaUpload(
        key,
        patientToken
      );
    } catch {
      return { ok: false, stage: "confirm", response: undefined };
    }

    if (
      !confirmResponse ||
      confirmResponse.status < 200 ||
      confirmResponse.status >= 300
    ) {
      return { ok: false, stage: "confirm", response: confirmResponse };
    }

    return { ok: true, key };
  },

  async deleteTempSurveyAnswerMedia(mediaPath: string): Promise<AxiosResponse> {
    const url = `/Portal/DynamicSurveys/UserSurveys/Answers/Media`;

    const serverResponse = await api
      .delete(url, {
        params: { mediaPath },
      })
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse) {
      return serverResponse;
    }

    throw Error;
  },

  // Mobile

  async getAssignedSurveysMobile(
    token: string,
    tokenType: string = "Bearer"
  ): Promise<AxiosResponse<IUserSurveyRequest[]>> {
    const url = `/Mobile/DynamicSurveys/UserSurveys`;

    return api.get<IUserSurveyRequest[]>(url, {
      headers: {
        Authorization: `${tokenType} ${token}`,
      },
    });
  },

  async getSurveyByIdMobile(surveyId: string): Promise<AxiosResponse> {
    const url = `/Mobile/DynamicSurveys/Surveys/${surveyId}`;
    const survey = await api.get(url);
    return survey;
  },

  async postUserSurveyAnswersMobile(
    userSurveyId: string,
    answersToSend: ISurveyAnswer[],
    patientUserId?: number,
    patientToken?: string
  ): Promise<AxiosResponse> {
    const url = `/Mobile/DynamicSurveys/UserSurveys/${userSurveyId}/Answers`;
    const objectToSend = {
      answers: answersToSend,
      patientUserId,
    };
    const serverResponse = await api
      .post<ISurvey>(url, objectToSend, patientAuth(patientToken))
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse) {
      return serverResponse;
    }

    throw Error;
  },
};
